// kware start edit - Offline Operations: Main page
// REASON: Provides a dedicated UI for staff to upload and process offline operations
// (check-out, check-in, renew) that occurred during system downtime.
// Written as class component to avoid infinite re-render caused by Pane/MCLRenderer
// when footer/title props are recreated as new objects on every functional render.

import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { get } from 'lodash';
import {
  Pane,
  Paneset,
  PaneFooter,
  Button,
  ButtonGroup,
  Select,
  MessageBanner,
  Icon,
  Badge,
} from '@folio/stripes/components';
import { stripesConnect, withStripes } from '@folio/stripes/core';

import { parseExcelFile, OPERATION_TYPES } from './ExcelParser';
import { processBatch } from './BatchProcessor';
import OperationsPreviewTable from './OperationsPreviewTable';
import ResultsTable from './ResultsTable';

import css from './OfflineOperations.css';

const STEPS = {
  UPLOAD: 'UPLOAD',
  PREVIEW: 'PREVIEW',
  PROCESSING: 'PROCESSING',
  RESULTS: 'RESULTS',
};

const TABS = [
  { key: OPERATION_TYPES.CHECK_OUT, labelId: 'ui-checkout.offline.type.checkOut' },
  { key: OPERATION_TYPES.RENEW, labelId: 'ui-checkout.offline.type.renew' },
  { key: OPERATION_TYPES.CHECK_IN, labelId: 'ui-checkout.offline.type.checkIn' },
];

class OfflineOperations extends React.Component {
  static manifest = Object.freeze({
    servicePoints: {
      type: 'okapi',
      records: 'servicepoints',
      path: 'service-points?query=cql.allRecords=1&limit=100',
    },
    checkOut: {
      type: 'okapi',
      path: 'circulation/check-out-by-barcode',
      fetch: false,
      accumulate: true,
    },
    checkIn: {
      type: 'okapi',
      path: 'circulation/check-in-by-barcode',
      fetch: false,
      accumulate: true,
    },
    renew: {
      type: 'okapi',
      path: 'circulation/renew-by-barcode',
      fetch: false,
      accumulate: true,
    },
    addInfo: {
      type: 'okapi',
      path: 'circulation/loans/:{loanId}/add-info',
      fetch: false,
      accumulate: true,
    },
  });

  static propTypes = {
    mutator: PropTypes.shape({
      checkOut: PropTypes.shape({ POST: PropTypes.func.isRequired }),
      checkIn: PropTypes.shape({ POST: PropTypes.func.isRequired }),
      renew: PropTypes.shape({ POST: PropTypes.func.isRequired }),
      addInfo: PropTypes.shape({ POST: PropTypes.func.isRequired }),
    }).isRequired,
    resources: PropTypes.shape({
      servicePoints: PropTypes.shape({ records: PropTypes.arrayOf(PropTypes.object) }),
    }).isRequired,
    stripes: PropTypes.object.isRequired,
    intl: PropTypes.object.isRequired,
    history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
    match: PropTypes.shape({ path: PropTypes.string.isRequired }).isRequired,
  };

  constructor(props) {
    super(props);
    this.uploadDate = new Date().toISOString().split('T')[0];
    // Get current service point from logged-in staff user automatically
    const currentSpId = get(props.stripes, ['user', 'user', 'curServicePoint', 'id'], '');
    this.state = {
      step: STEPS.UPLOAD,
      operations: [],
      summary: null,
      servicePointId: currentSpId,
      results: [],
      parseError: null,
      progress: { current: 0, total: 0 },
      activeTab: OPERATION_TYPES.CHECK_OUT,
    };
  }

  getStaffName = () => get(this.props.stripes, ['user', 'user', 'username'], 'unknown');

  buildComment = (op) => {
    const parts = [
      `[OFFLINE] Uploaded on ${this.uploadDate} by ${this.getStaffName()}.`,
      `Original action date: ${op.actionDate}.`,
    ];
    if (op.note) parts.push(`Staff note: ${op.note}`);
    return parts.join(' ');
  }

  getPreviewComment = () =>
    `[OFFLINE] Uploaded on ${this.uploadDate} by ${this.getStaffName()}. Original action date: {actionDate}.`;

  handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    this.setState({ parseError: null });
    try {
      const { operations, summary } = await parseExcelFile(file);
      const firstNonEmpty = TABS.find(t => operations.some(o => o.type === t.key));
      this.setState({
        operations,
        summary,
        activeTab: firstNonEmpty ? firstNonEmpty.key : OPERATION_TYPES.CHECK_OUT,
        step: STEPS.PREVIEW,
      });
    } catch (err) {
      this.setState({ parseError: err.message || 'Failed to parse file' });
    }
  }

  handleExecute = async () => {
    const { mutator } = this.props;
    const { operations, servicePointId } = this.state;
    if (!servicePointId) return;

    this.setState({ step: STEPS.PROCESSING, progress: { current: 0, total: operations.length } });

    const processedResults = [];
    let current = 0;

    for (const op of operations) {
      // eslint-disable-next-line no-await-in-loop
      const [result] = await processBatch([op], {
        mutator,
        servicePointId,
        buildComment: this.buildComment,
      });
      processedResults.push(result);
      current += 1;
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => {
        this.setState({ progress: { current, total: operations.length } }, resolve);
      });
    }

    this.setState({ results: processedResults, step: STEPS.RESULTS });
  }

  handleReset = () => {
    this.uploadDate = new Date().toISOString().split('T')[0];
    this.setState({
      step: STEPS.UPLOAD,
      operations: [],
      summary: null,
      servicePointId: '',
      results: [],
      parseError: null,
      progress: { current: 0, total: 0 },
      activeTab: OPERATION_TYPES.CHECK_OUT,
    });
  }

  handleBack = () => {
    this.props.history.push(this.props.match.path.replace('/offline-operations', ''));
  }

  renderUploadStep() {
    const { parseError } = this.state;
    return (
      <div className={css.uploadSection}>
        <MessageBanner type="info">
          <FormattedMessage id="ui-checkout.offline.uploadInstructions" />
        </MessageBanner>
        <div className={css.uploadBox}>
          <Icon icon="upload" size="large" />
          <p><FormattedMessage id="ui-checkout.offline.uploadLabel" /></p>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={this.handleFileChange}
            className={css.fileInput}
            id="offline-file-input"
          />
          <label htmlFor="offline-file-input" className={css.uploadButton}>
            <FormattedMessage id="ui-checkout.offline.chooseFile" />
          </label>
        </div>
        {parseError && <MessageBanner type="error">{parseError}</MessageBanner>}
      </div>
    );
  }

  renderPreviewStep() {
    const { operations, summary, servicePointId, activeTab } = this.state;
    const { resources } = this.props;
    const servicePoints = get(resources, ['servicePoints', 'records'], []);
    const currentSp = servicePoints.find(sp => sp.id === servicePointId);

    const tabCounts = {
      [OPERATION_TYPES.CHECK_OUT]: summary?.checkOut ?? 0,
      [OPERATION_TYPES.RENEW]: summary?.renew ?? 0,
      [OPERATION_TYPES.CHECK_IN]: summary?.checkIn ?? 0,
    };

    const tabOps = operations.filter(o => o.type === activeTab);

    return (
      <div>
        {/* kware start editing
          * REASON: Show current service point automatically instead of a dropdown.
          * Service point is taken from stripes.user.user.curServicePoint like CheckOut.js does.
          */}
        <div className={css.servicePointRow}>
          <strong><FormattedMessage id="ui-checkout.offline.servicePoint" />:</strong>
          &nbsp;
          {currentSp
            ? this.props.intl.formatMessage({
              id: `ui-tenant-settings.servicePoints.name.${currentSp.name}`,
              defaultMessage: currentSp.name,
            })
            : servicePointId || <FormattedMessage id="ui-checkout.offline.noServicePoint" />}
        </div>
        {/* kware end editing */}
        <ButtonGroup fullWidth>
          {TABS.map(tab => (
            <Button
              key={tab.key}
              onClick={() => this.setState({ activeTab: tab.key })}
              buttonStyle={activeTab === tab.key ? 'primary' : 'default'}
              aria-selected={activeTab === tab.key}
            >
              <FormattedMessage id={tab.labelId} />
              &nbsp;
              <Badge>{tabCounts[tab.key]}</Badge>
            </Button>
          ))}
        </ButtonGroup>
        <div className={css.tableWrapper}>
          <OperationsPreviewTable
            operations={tabOps}
            offlineComment={this.buildComment}
          />
        </div>
      </div>
    );
  }

  renderProcessingStep() {
    const { progress } = this.state;
    const pct = progress.total ? (progress.current / progress.total) * 100 : 0;
    return (
      <div className={css.processingSection}>
        <Icon icon="spinner-ellipsis" size="large" />
        <h3>
          <FormattedMessage
            id="ui-checkout.offline.processing"
            values={{ current: progress.current, total: progress.total }}
          />
        </h3>
        <div className={css.progressBar}>
          <div className={css.progressFill} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }

  renderResultsStep() {
    const { results } = this.state;
    return <ResultsTable results={results} />;
  }

  render() {
    const { step, operations, servicePointId } = this.state;

    const uploadFooter = (
      <PaneFooter
        renderStart={
          <Button marginBottom0 onClick={this.handleBack}>
            <FormattedMessage id="ui-checkout.offline.button.back" />
          </Button>
        }
      />
    );

    const previewFooter = (
      <PaneFooter
        renderStart={
          <Button marginBottom0 onClick={this.handleReset}>
            <FormattedMessage id="ui-checkout.offline.button.reset" />
          </Button>
        }
        renderEnd={
          <Button
            marginBottom0
            buttonStyle="primary"
            disabled={operations.length === 0}
            onClick={this.handleExecute}
          >
            <FormattedMessage
              id="ui-checkout.offline.button.execute"
              values={{ count: operations.length }}
            />
          </Button>
        }
      />
    );

    const resultsFooter = (
      <PaneFooter
        renderEnd={
          <Button marginBottom0 buttonStyle="primary" onClick={this.handleReset}>
            <FormattedMessage id="ui-checkout.offline.button.newSession" />
          </Button>
        }
      />
    );

    const footerMap = {
      [STEPS.UPLOAD]: uploadFooter,
      [STEPS.PREVIEW]: previewFooter,
      [STEPS.PROCESSING]: null,
      [STEPS.RESULTS]: resultsFooter,
    };

    const titleMap = {
      [STEPS.UPLOAD]: <FormattedMessage id="ui-checkout.offline.title" />,
      [STEPS.PREVIEW]: <FormattedMessage id="ui-checkout.offline.title.preview" values={{ count: operations.length }} />,
      [STEPS.PROCESSING]: <FormattedMessage id="ui-checkout.offline.title.processing" />,
      [STEPS.RESULTS]: <FormattedMessage id="ui-checkout.offline.title.results" />,
    };

    const contentMap = {
      [STEPS.UPLOAD]: () => this.renderUploadStep(),
      [STEPS.PREVIEW]: () => this.renderPreviewStep(),
      [STEPS.PROCESSING]: () => this.renderProcessingStep(),
      [STEPS.RESULTS]: () => this.renderResultsStep(),
    };

    return (
      <Paneset>
        <Pane
          defaultWidth="100%"
          paneTitle={titleMap[step]}
          footer={footerMap[step]}
        >
          {contentMap[step]?.()}
        </Pane>
      </Paneset>
    );
  }
}

export default injectIntl(withStripes(stripesConnect(OfflineOperations)));
// kware end edit
